// CB:BEGIN admin-command-center
/**
 * AdminCommandCenter — Redesigned main admin dashboard content.
 *
 * Layout (top to bottom):
 *   1. KPI metric strip (AdminStatsCards)
 *   2. Stalled project alerts (AdminAlertsPanel) — only visible if alerts exist
 *   3. Project health bar (AdminHealthCards)
 *   4. Quick actions grid — 8 primary actions, organized by workflow stage
 */
import React from 'react';
import AdminStatsCards from './AdminStatsCards';
import AdminAlertsPanel from './AdminAlertsPanel';
import AdminHealthCards from './AdminHealthCards';

interface Action {
  id: string;
  label: string;
  description: string;
  screen: string;
  gradient: string;
  icon: React.ReactNode;
  priority?: 'primary' | 'secondary';
}

// ─── SVG icons ────────────────────────────────────────────────────────────────

const IconHouse = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);
const IconWrench = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);
const IconFolder = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
  </svg>
);
const IconShield = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);
const IconDollar = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
  </svg>
);
const IconSearch = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);
const IconChart = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);
const IconDocuments = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

// ─── Quick actions ────────────────────────────────────────────────────────────

const QUICK_ACTIONS: Action[] = [
  {
    id: 'homeowners',
    label: 'Homeowners',
    description: 'View, search, and manage homeowner accounts',
    screen: 'admin-homeowners-list',
    gradient: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    icon: <IconHouse />,
    priority: 'primary',
  },
  {
    id: 'contractors',
    label: 'Contractors',
    description: 'Verify contractors, manage compliance and profiles',
    screen: 'admin-contractors-list',
    gradient: 'linear-gradient(135deg, #059669, #047857)',
    icon: <IconWrench />,
    priority: 'primary',
  },
  {
    id: 'projects',
    label: 'Projects',
    description: 'View and manage all active and completed projects',
    screen: 'admin-projects-list',
    gradient: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
    icon: <IconFolder />,
    priority: 'primary',
  },
  {
    id: 'verification',
    label: 'Verification Queue',
    description: 'Review pending contractor verification submissions',
    screen: 'admin-verification-queue',
    gradient: 'linear-gradient(135deg, #d97706, #b45309)',
    icon: <IconShield />,
    priority: 'primary',
  },
  {
    id: 'finance',
    label: 'Finance',
    description: 'Platform revenue, escrow balances, invoices',
    screen: 'admin-finance',
    gradient: 'linear-gradient(135deg, #0284c7, #0369a1)',
    icon: <IconDollar />,
  },
  {
    id: 'operations',
    label: 'Operations',
    description: 'Search projects by ID, address, email, or escrow ID',
    screen: 'admin-operations',
    gradient: 'linear-gradient(135deg, #374151, #1f2937)',
    icon: <IconSearch />,
  },
  {
    id: 'reviews',
    label: 'Reviews',
    description: 'Moderate homeowner reviews of contractors',
    screen: 'admin-reviews',
    gradient: 'linear-gradient(135deg, #db2777, #be185d)',
    icon: <IconChart />,
  },
  {
    id: 'documents',
    label: 'Documents',
    description: 'View and manage platform documents',
    screen: 'admin-documents',
    gradient: 'linear-gradient(135deg, #0891b2, #0e7490)',
    icon: <IconDocuments />,
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface AdminCommandCenterProps {
  onNavigate?: (screen: string) => void;
}

const AdminCommandCenter: React.FC<AdminCommandCenterProps> = ({ onNavigate }) => {
  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Command Center</h1>
          <p style={{ fontSize: 13, color: '#9ca3af', margin: '4px 0 0 0' }}>CoreBids platform overview</p>
        </div>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>{dateStr} · {timeStr}</span>
      </div>

      {/* ── KPI strip ── */}
      <section style={{ marginBottom: 20 }}>
        <AdminStatsCards onNavigate={onNavigate} />
      </section>

      {/* ── Stalled alerts (shown only when > 0) ── */}
      <AdminAlertsPanel onNavigate={onNavigate} />

      {/* ── Project health ── */}
      <section style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        padding: '16px 18px',
        marginBottom: 24,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <AdminHealthCards onNavigate={onNavigate} />
      </section>

      {/* ── Quick actions ── */}
      <section>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Quick Access</span>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 10,
        }}>
          {QUICK_ACTIONS.map((action) => (
            <button
              key={action.id}
              type="button"
              onClick={() => onNavigate?.(action.screen)}
              data-testid={`qa-${action.id}`}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: '14px 16px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'box-shadow 0.12s, border-color 0.12s',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.boxShadow = '0 3px 10px rgba(0,0,0,0.08)';
                el.style.borderColor = '#c7d2fe';
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.boxShadow = 'none';
                el.style.borderColor = '#e5e7eb';
              }}
            >
              {/* Icon */}
              <div style={{
                width: 38, height: 38, flexShrink: 0,
                background: action.gradient, borderRadius: 9,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {action.icon}
              </div>

              {/* Text */}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 2 }}>
                  {action.label}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
                  {action.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

    </div>
  );
};

export default AdminCommandCenter;
// CB:END admin-command-center
